import { Assert } from '@cotera/utilities';
import { z } from 'zod/v3';

export enum ResourceType {
  Chat = 'chat',
  Agent = 'agent',
  Folder = 'folder',
  Document = 'document',
  Workflow = 'workflow',
  Dataset = 'dataset',
  /** Persisted `workflow_triggers_cron` / `workflow_triggers_webhook` rows (binding id = trigger id). */
  WorkflowTrigger = 'workflow_trigger',
}

/**
 * How a dataset's rows come to exist. Kinds differ in where the first artifact
 * comes from and in which capabilities they support; everything downstream of
 * the artifact is shared.
 */
export enum DatasetType {
  /** Parsed from an uploaded CSV. */
  Csv = 'csv',
  /** Authored in the product: columns and rows the user creates or imports. */
  Manual = 'manual',
  /**
   * A dataset seeded from a SELECT against the org's connected **Snowflake**
   * warehouse and kept in step with it: source changes are reflected into the
   * capped parquet preview as operations, and the org can query the full copy in
   * their own warehouse.
   *
   * Legacy rows written before the per-vendor split persisted this as
   * `'warehouse'`; the restamp migration rewrites them to `'snowflake'`, and the
   * `Dataset` entity reads any straggler `'warehouse'` back as this kind.
   */
  Snowflake = 'snowflake',
  /**
   * The Snowflake kind's twin for an org whose connected warehouse is Postgres.
   * Same engine — both run through the ERA dialect, which is where every
   * vendor difference already lives — so the two kinds differ only in which
   * warehouse the connection points at. Which of the two a new dataset gets is
   * chosen from the org's `WarehouseConnection` vendor at create time.
   */
  Postgres = 'postgres',
  /**
   * The same kind again for an org whose connected warehouse is BigQuery. Like
   * the other two it runs through the ERA dialect, so the vendor difference
   * lives there rather than here — quoting (backticks), the `project.dataset`
   * two-part schema, and named `@paramsN` binds are all the dialect's business.
   */
  BigQuery = 'bigquery',
}

/**
 * The dataset kinds backed by the org's connected warehouse rather than a
 * parquet artifact authored in the product. They share one strategy
 * implementation and differ only in the connection vendor behind them; code
 * that treats "any warehouse dataset" alike (change detection scheduling, the
 * warehouse-only HTTP routes, the grid's repopulate control) keys on this set
 * rather than naming a single kind.
 */
export type WarehouseDatasetType =
  | DatasetType.Snowflake
  | DatasetType.Postgres
  | DatasetType.BigQuery;

/**
 * Whether `type` is one of the warehouse-backed kinds.
 *
 * An exhaustive switch rather than a lookup in a hand-maintained list, because
 * that list is exactly what a new vendor gets left out of — and being left out
 * fails silently at every site this gates: the warehouse-only routes reject the
 * dataset, reseed refuses it, change evaluation never schedules it. A new
 * {@link DatasetType} breaks this switch at compile time instead.
 */
export const isWarehouseDatasetType = (
  type: DatasetType
): type is WarehouseDatasetType => {
  switch (type) {
    case DatasetType.Csv:
    case DatasetType.Manual:
      return false;
    case DatasetType.Snowflake:
    case DatasetType.Postgres:
    case DatasetType.BigQuery:
      return true;
    default:
      return Assert.unreachable(type);
  }
};

/** Derived from {@link isWarehouseDatasetType}, so the two cannot disagree. */
export const WAREHOUSE_DATASET_TYPES: readonly WarehouseDatasetType[] =
  Object.values(DatasetType).filter(isWarehouseDatasetType);

/**
 * What a dataset trigger watches for. Identical for every {@link DatasetType} —
 * how candidate rows are produced is the dataset's business, so kind never
 * reaches the trigger. Labels and helpers live in the SDK, which re-exports
 * this; the values live here because the SDK depends on this package.
 */
export const DATASET_TRIGGER_CRITERIA = [
  'new_row',
  'record_changed',
  'record_first_meets_conditions',
  'row_deleted',
] as const;

export type DatasetTriggerCriterion = (typeof DATASET_TRIGGER_CRITERIA)[number];

/**
 * Whether a dataset trigger acts on the changes it detects, or only ever when a
 * user asks it to.
 *
 * Orthogonal to {@link DATASET_TRIGGER_CRITERIA} rather than another value in
 * it: the criterion says *which* rows a run is about, and that question still
 * has to be answered for a manual run — the dataset grid's Run menu replays rows
 * through the trigger's criterion exactly as a scheduled evaluation would.
 * Folding "manual" in as a fifth criterion would make those two orthogonal
 * choices mutually exclusive.
 *
 * `manual` is not the same as disabling the trigger. A disabled trigger is not
 * evaluated at all and its bindings go dark; a manual one stays bound, stays
 * visible in the dataset's automations column, and keeps its baseline current so
 * switching it back to `automatic` means "from here on" rather than "everything
 * that changed while I was not looking".
 */
export const DATASET_TRIGGER_RUN_MODES = ['automatic', 'manual'] as const;

export type DatasetTriggerRunMode = (typeof DATASET_TRIGGER_RUN_MODES)[number];

const resourceTypeSchema = z.nativeEnum(ResourceType);
// Deliberately not exported: this file is on zod/v3, and a v3 schema embedded
// in a v4 one breaks `toJSONSchema` at OpenAPI generation time. Files build
// their own schema from the enum with whichever `z` they import.
const datasetTypeSchema = z.nativeEnum(DatasetType);

const resourceGetParamsSchema = z.object({
  type: resourceTypeSchema,
  id: z.string().uuid(),
});

const resourcePermissionLevelSchema = z.enum([
  'view',
  'edit',
  'admin',
  'pending',
]);

/** Scope of a folder from the current user's perspective: user (private), org, or external. */
const resourceScopeSchema = z.enum(['user', 'org', 'external']);

/** TipTap document state (agent editor). */
const documentStateSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    type: z.literal('doc'),
    content: z.array(z.unknown()).optional(),
  })
);

const persistedWorkflowGraphSchema = z.object({
  nodes: z.array(
    z.object({
      id: z.string().uuid(),
      ownerId: z.string().uuid(),
      ownerOrgId: z.string().uuid(),
      workflowId: z.string().uuid(),
      type: z.string(),
      data: z.unknown(),
      inputShape: z.string().nullable(),
      defaultOutput: z.unknown(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
  ),
  edges: z.array(
    z.object({
      id: z.string().uuid(),
      ownerId: z.string().uuid(),
      ownerOrgId: z.string().uuid(),
      workflowId: z.string().uuid(),
      fromNodeId: z.string().uuid(),
      toNodeId: z.string().uuid(),
      condition: z.unknown(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
  ),
  triggerBindings: z.array(
    z.object({
      id: z.string().uuid(),
      ownerId: z.string().uuid(),
      ownerOrgId: z.string().uuid(),
      triggerType: z.string(),
      triggerId: z.string().uuid(),
      workflowId: z.string().uuid(),
      enabled: z.boolean(),
      priority: z.number().int(),
      defaultInput: z.unknown(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
  ),
  triggers: z.array(
    z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('cron'),
        trigger: z.object({
          id: z.string().uuid(),
          ownerId: z.string().uuid(),
          ownerOrgId: z.string().uuid(),
          name: z.string(),
          cron: z.string(),
          timezone: z.string(),
          enabled: z.boolean(),
          createdAt: z.string(),
          updatedAt: z.string(),
        }),
      }),
      z.object({
        kind: z.literal('webhook'),
        trigger: z.object({
          id: z.string().uuid(),
          ownerId: z.string().uuid(),
          ownerOrgId: z.string().uuid(),
          name: z.string(),
          /** Non-sensitive indicator only; webhook secrets are never returned. */
          hasSecret: z.boolean().optional(),
          enabled: z.boolean(),
          createdAt: z.string(),
          updatedAt: z.string(),
        }),
      }),
      z.object({
        kind: z.literal('event'),
        trigger: z.object({
          id: z.string().uuid(),
          ownerId: z.string().uuid(),
          ownerOrgId: z.string().uuid(),
          name: z.string(),
          eventName: z.string(),
          enabled: z.boolean(),
          createdAt: z.string(),
          updatedAt: z.string(),
        }),
      }),
      z.object({
        kind: z.literal('dataset'),
        trigger: z.object({
          id: z.string().uuid(),
          ownerId: z.string().uuid(),
          ownerOrgId: z.string().uuid(),
          name: z.string(),
          datasetId: z.string().uuid(),
          criterion: z.enum(DATASET_TRIGGER_CRITERIA),
          /** Null means every non-internal column, resolved at evaluation time. */
          trackFields: z.array(z.string()).nullable(),
          filter: z.unknown(),
          processExistingRows: z.boolean(),
          runMode: z.enum(DATASET_TRIGGER_RUN_MODES),
          enabled: z.boolean(),
          createdAt: z.string(),
          updatedAt: z.string(),
        }),
      }),
    ])
  ),
});

/**
 * A principal that has access to a resource. An org-level grant carries the
 * org it is for, since that isn't always the viewer's.
 */
const resourceAccessEntrySchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('user'), id: z.string().uuid() }),
  z.object({ t: z.literal('org'), orgId: z.string().uuid().optional() }),
]);

export type ResourceAccessEntry = z.infer<typeof resourceAccessEntrySchema>;

/** Folder membership and permission for chat/agent (included in get response). */
const resourceMetaSchema = z.object({
  /** Folder this resource is in, if any. Null when not in a folder. */
  folderId: z.string().uuid().nullable(),
  /** Scope of that folder when in a folder (user = private, org = shared with org, external = other shared). Null when not in a folder. */
  folderScope: resourceScopeSchema.nullable().optional(),
  /** Current user's effective permission on this resource. */
  permission: resourcePermissionLevelSchema,
  /** Chat only: whether this chat has unread assistant messages. */
  hasUnreadAssistant: z.boolean().optional(),
  /** Users and orgs that have access to this resource (owner + explicit grants). */
  users: z.array(resourceAccessEntrySchema).optional(),
});

/** Chat payload for resource get response. */
const serializedConversationSchema = z.object({
  id: z.string(),
  orgId: z.string().uuid(),
  actorId: z.string().uuid(),
  agent: z.string().nullable(),
  agentId: z.string().uuid().nullable().optional(),
  title: z.string(),
  status: z.enum(['active', 'archived']),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  hasUnreadAssistant: z.boolean().optional(),
  triggerSource: z
    .enum([
      'chat',
      'chat-bot',
      'slack',
      'linear',
      'webhook',
      'warehouse',
      'cron',
      'event',
      'dataset',
      'compaction-summary',
      'workflow',
      'system',
      'api',
    ])
    .nullable()
    .optional(),
});

const datasetArtifactMetadataSchema = z.object({
  rowCount: z.number().int().nonnegative().nullable(),
  sourceRowCount: z.number().int().nonnegative().nullable().optional(),
  estimatedSizeBytes: z.number().int().nonnegative().nullable(),
  schema: z.object({
    columns: z.array(
      z.object({
        name: z.string(),
        type: z.string().nullable(),
      })
    ),
  }),
});

const datasetArtifactSchema = z.object({
  id: z.string().uuid(),
  datasetId: z.string().uuid(),
  pointer: z.string(),
  hash: z.string(),
  metadata: datasetArtifactMetadataSchema.nullable().optional(),
  expiresAt: z.date({ coerce: true }).nullable(),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  lastAccessedAt: z.date({ coerce: true }).nullable(),
});

const datasetSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  ownerId: z.string().uuid(),
  type: datasetTypeSchema,
  name: z.string(),
  originalFilename: z.string().nullable(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  /**
   * Declared row key. Null falls back to an internal, import-unstable id — as
   * does a declaration naming that id, which is what a CSV import writes.
   */
  rowKeyColumns: z.array(z.string()).nullable().optional(),
  /**
   * Where the dataset's rows are in getting here. A CSV import completes after
   * the request that started it, so `importing` distinguishes a dataset whose
   * rows are still being read from one that genuinely has none.
   */
  importStatus: z.enum(['importing', 'ready', 'failed']).optional(),
  importError: z.string().nullable().optional(),
  status: z.enum(['active', 'archived']),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  artifacts: z.array(datasetArtifactSchema).optional(),
});

/** Discriminated union: one of chat, agent, or folder payload. Chat/agent include meta (folder + permission). */
const resourceOutputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('chat'),
    chat: serializedConversationSchema,
    meta: resourceMetaSchema.optional(),
  }),
  z.object({
    type: z.literal('agent'),
    agent: z.object({
      id: z.string().uuid(),
      orgId: z.string().uuid(),
      ownerId: z.string().uuid(),
      agentKey: z.string(),
      editorState: documentStateSchema,
      model: z.string(),
      locked: z.boolean(),
      createdAt: z.date({ coerce: true }),
      updatedAt: z.date({ coerce: true }),
      title: z.string().nullable(),
      description: z.string().nullable(),
      type: z.string().nullable(),
      status: z.enum(['active', 'archived']),
      /** User-provided emoji override shown in place of the default agent icon. */
      emoji: z.string().max(16).nullable().optional(),
      /** The conversation this agent was created in, or null if not chat-created. */
      sourceConversationId: z.string().uuid().nullable().optional(),
    }),
    meta: resourceMetaSchema.optional(),
  }),
  z.object({
    type: z.literal('folder'),
    folder: z.object({
      id: z.string().uuid(),
      ownerId: z.string().uuid(),
      parentId: z.string().uuid().nullable(),
      name: z.string(),
      position: z.number(),
      createdAt: z.date({ coerce: true }),
      updatedAt: z.date({ coerce: true }),
      /** Scope from current user's perspective: user (private), org, or external. */
      scope: resourceScopeSchema,
      /** Optional extra scope-related data (e.g. shared-with details). */
      scopeData: z.record(z.unknown()).optional(),
      /**
       * Direct children the Library lists as rows (subfolders + artifacts),
       * visible in this org context. Chats are not among them — they are never
       * listed individually, so their presence is flagged by `chatCount` instead.
       */
      childrenCount: z.number().int().min(0).optional(),
      /**
       * Whether the folder holds any chats, as `1` or `0` — not how many. Chats
       * collapse to one row regardless of count, so only their presence is
       * reported.
       */
      chatCount: z.number().int().min(0).optional(),
    }),
  }),
  z.object({
    type: z.literal('document'),
    document: z.object({
      id: z.string().uuid(),
      orgId: z.string().uuid(),
      ownerId: z.string().uuid(),
      editorState: documentStateSchema,
      locked: z.boolean(),
      title: z.string().nullable(),
      description: z.string().nullable(),
      status: z.enum(['active', 'archived']),
      createdAt: z.date({ coerce: true }),
      updatedAt: z.date({ coerce: true }),
      /** The conversation this document was created in, or null if not chat-created. */
      sourceConversationId: z.string().uuid().nullable().optional(),
    }),
    meta: resourceMetaSchema.optional(),
  }),
  z.object({
    type: z.literal('workflow'),
    workflow: z.object({
      id: z.string().uuid(),
      orgId: z.string().uuid(),
      ownerId: z.string().uuid(),
      name: z.string(),
      status: z.enum(['active', 'archived']),
      workflowStatus: z.string().optional(),
      workflowType: z.string().optional(),
      idempotencyKey: z
        .union([
          z.object({
            strategy: z.literal('value'),
            pointer: z.string().min(1),
          }),
          z.object({
            strategy: z.literal('value'),
            template: z.string().min(1),
          }),
        ])
        .nullable()
        .optional(),
      graph: persistedWorkflowGraphSchema.optional(),
      createdAt: z.date({ coerce: true }),
      updatedAt: z.date({ coerce: true }),
      /** Present for cron + agent (scheduled-agent) workflows. */
      schedule: z.string().optional(),
      timezone: z.string().optional(),
      cronTriggerId: z.string().uuid().optional(),
      agentId: z.string().uuid().nullable().optional(),
      agentTitle: z.string().nullable().optional(),
      prompt: z.string().optional(),
      /** Display names of all bound triggers, so Library "All" search matches any trigger name at parity with the Workflows list (which matches every binding via SQL). Ordered primary-first. */
      triggerNames: z.array(z.string()).optional(),
    }),
    meta: resourceMetaSchema.optional(),
  }),
  z.object({
    type: z.literal('dataset'),
    dataset: datasetSchema,
    meta: resourceMetaSchema.optional(),
  }),
  z.object({
    type: z.literal('workflow_trigger'),
    workflowTrigger: z.object({
      id: z.string().uuid(),
      orgId: z.string().uuid(),
      ownerId: z.string().uuid(),
      name: z.string(),
      enabled: z.boolean(),
      triggerKind: z.enum(['cron', 'webhook', 'event', 'dataset']),
      status: z.enum(['active', 'archived']),
      createdAt: z.date({ coerce: true }),
      updatedAt: z.date({ coerce: true }),
      cron: z
        .object({
          schedule: z.string(),
          timezone: z.string(),
          enabled: z.boolean(),
        })
        .nullable(),
      /** Webhook row flags only; secret is never returned. */
      webhook: z
        .object({
          enabled: z.boolean(),
        })
        .nullable(),
      /** Event trigger listen name; null for cron/webhook. */
      event: z
        .object({
          eventName: z.string(),
          enabled: z.boolean(),
        })
        .nullable()
        .optional(),
      /** Dataset trigger config; null for cron/webhook/event. */
      dataset: z
        .object({
          datasetId: z.string().uuid(),
          criterion: z.enum(DATASET_TRIGGER_CRITERIA),
          /** Null means every non-internal column. */
          trackFields: z.array(z.string()).nullable(),
          filter: z.unknown(),
          processExistingRows: z.boolean(),
          runMode: z.enum(DATASET_TRIGGER_RUN_MODES),
          /**
           * Why the last evaluation refused to run this trigger — typically a
           * column its filter or watched fields name that the dataset no longer
           * has. Null when healthy.
           */
          brokenReason: z.string().nullable(),
          enabled: z.boolean(),
        })
        .nullable()
        .optional(),
      /** Webhook source app (e.g. `hubspot`, `github`); `generic` for a custom webhook. Null for cron. */
      webhookProviderKey: z.string().nullable().optional(),
      /**
       * The generated address an `email` webhook listens on. It is what the
       * trigger *is* — a workflow run per message delivered here — so lists name
       * the row by it rather than by a user-supplied name.
       *
       * Null for every other provider. Treat it as sensitive: inbound email
       * carries no signature, so the address is the only thing standing between
       * a stranger and a workflow run.
       */
      inboundEmailAddress: z.string().nullable().optional(),
      /**
       * Which of the provider's events this webhook listens for (e.g.
       * `pull_request.opened`). Resolved against the provider catalog to name
       * the trigger by what it reacts to. Null for custom webhooks.
       */
      webhookEventKey: z.string().nullable().optional(),
    }),
    meta: resourceMetaSchema.optional(),
  }),
]);

/** Message shape for lastMessage in chat list (optional in folder items). */
const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ])
);

const storedMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.string(),
  position: z.number(),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  usage: jsonValueSchema.nullable(),
  message: jsonValueSchema,
});

/** Single item shapes for list (same as get payload per type). Chat list includes lastMessage; folder items omit it. */
const chatItemSchema = z.object({
  id: z.string(),
  orgId: z.string().uuid(),
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName: z.string().nullable().optional(),
  actorId: z.string().uuid(),
  agent: z.string().nullable(),
  agentId: z.string().uuid().nullable().optional(),
  title: z.string(),
  status: z.enum(['active', 'archived']),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  hasUnreadAssistant: z.boolean().optional(),
  lastMessage: storedMessageSchema.nullable().optional(),
  firstMessage: storedMessageSchema.nullable().optional(),
  folderId: z.string().uuid().nullable().optional(),
  folderName: z.string().nullable().optional(),
  folderScope: resourceScopeSchema.nullable().optional(),
  triggerSource: z
    .enum([
      'chat',
      'chat-bot',
      'slack',
      'linear',
      'webhook',
      'warehouse',
      'cron',
      'event',
      'dataset',
      'compaction-summary',
      'workflow',
      'system',
      'api',
    ])
    .nullable()
    .optional(),
  /** Owner + explicit grantees, for "shared with N" + who-has-access hover. */
  users: z.array(resourceAccessEntrySchema).optional(),
});

/** Agent list/folder item; title is optional (display name from editor state). */
const agentItemSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName: z.string().nullable().optional(),
  ownerId: z.string().uuid(),
  agentKey: z.string(),
  model: z.string(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  status: z.enum(['active', 'archived']),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  title: z.string().nullable().optional(),
  folderId: z.string().uuid().nullable().optional(),
  folderName: z.string().nullable().optional(),
  /** Ancestor folder names (root → leaf) of the folder this item is filed in. */
  folderPath: z.array(z.string()).nullable().optional(),
  folderScope: resourceScopeSchema.nullable().optional(),
  /** User-provided emoji override shown in place of the default agent icon. */
  emoji: z.string().max(16).nullable().optional(),
  users: z.array(resourceAccessEntrySchema).optional(),
  /** The conversation this agent was created in, or null if not chat-created. */
  sourceConversationId: z.string().uuid().nullable().optional(),
  /** Title of the source conversation, resolved for display in grouped views. */
  sourceConversationTitle: z.string().nullable().optional(),
});

/** Document list/folder item. */
const documentItemSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName: z.string().nullable().optional(),
  ownerId: z.string().uuid(),
  locked: z.boolean().optional(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  status: z.enum(['active', 'archived']),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  folderId: z.string().uuid().nullable().optional(),
  folderName: z.string().nullable().optional(),
  /** Ancestor folder names (root → leaf) of the folder this item is filed in. */
  folderPath: z.array(z.string()).nullable().optional(),
  folderScope: resourceScopeSchema.nullable().optional(),
  /** Effective visibility scope for the viewer: folder scope combined with direct shares on the document itself. */
  scope: resourceScopeSchema.nullable().optional(),
  users: z.array(resourceAccessEntrySchema).optional(),
  /** The conversation this document was created in, or null if not chat-created. */
  sourceConversationId: z.string().uuid().nullable().optional(),
  /** Title of the source conversation, resolved for display in grouped views. */
  sourceConversationTitle: z.string().nullable().optional(),
});

/** Workflow definition list/folder item. */
const workflowItemSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName: z.string().nullable().optional(),
  ownerId: z.string().uuid(),
  name: z.string(),
  status: z.enum(['active', 'archived']),
  workflowStatus: z.string().optional(),
  workflowType: z.string().optional(),
  graph: persistedWorkflowGraphSchema.optional(),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  folderId: z.string().uuid().nullable().optional(),
  folderName: z.string().nullable().optional(),
  /** Ancestor folder names (root → leaf) of the folder this item is filed in. */
  folderPath: z.array(z.string()).nullable().optional(),
  folderScope: resourceScopeSchema.nullable().optional(),
  schedule: z.string().optional(),
  timezone: z.string().optional(),
  cronTriggerId: z.string().uuid().optional(),
  agentId: z.string().uuid().nullable().optional(),
  agentTitle: z.string().nullable().optional(),
  /** Display name of the workflow's primary bound trigger, if any. */
  triggerName: z.string().nullable().optional(),
  prompt: z.string().optional(),
  users: z.array(resourceAccessEntrySchema).optional(),
});

/** Persisted workflow trigger list/folder item, across every trigger kind. */
const workflowTriggerItemSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  ownerId: z.string().uuid(),
  name: z.string(),
  enabled: z.boolean(),
  triggerKind: z.enum(['cron', 'webhook', 'event', 'dataset']),
  status: z.enum(['active', 'archived']),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  folderId: z.string().uuid().nullable().optional(),
  folderName: z.string().nullable().optional(),
  folderScope: resourceScopeSchema.nullable().optional(),
  cronSchedule: z.string().optional(),
  cronTimezone: z.string().optional(),
  /** Free-form event name for event triggers. */
  eventName: z.string().optional(),
  /** Watched dataset for dataset triggers. */
  datasetId: z.string().uuid().optional(),
  /** Display name of the watched dataset, for dataset triggers. */
  datasetName: z.string().nullable().optional(),
  /** What the dataset trigger watches for. */
  datasetCriterion: z.enum(DATASET_TRIGGER_CRITERIA).optional(),
  /** Whether the dataset trigger fires on its own, or only when asked. */
  datasetRunMode: z.enum(DATASET_TRIGGER_RUN_MODES).optional(),
  /** Webhook source app (e.g. `hubspot`, `github`); `generic` for a custom webhook. Absent for cron. */
  webhookProviderKey: z.string().nullable().optional(),
  /** Address an `email` webhook listens on — the trigger's identity in lists. */
  inboundEmailAddress: z.string().nullable().optional(),
  /** Provider event this webhook listens for; resolved against the catalog for display. */
  webhookEventKey: z.string().nullable().optional(),
  /** Bound workflow when this cron row is linked via `workflow_trigger_workflows` (e.g. scheduled agent). */
  workflowId: z.string().uuid().optional(),
  agentId: z.string().uuid().nullable().optional(),
  agentTitle: z.string().nullable().optional(),
});

const datasetItemSchema = datasetSchema.omit({ artifacts: true }).extend({
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName: z.string().nullable().optional(),
  folderId: z.string().uuid().nullable().optional(),
  folderName: z.string().nullable().optional(),
  /** Ancestor folder names (root → leaf) of the folder this item is filed in. */
  folderPath: z.array(z.string()).nullable().optional(),
  folderScope: resourceScopeSchema.nullable().optional(),
  /** Effective visibility scope for the viewer: folder scope combined with direct shares on the dataset itself. */
  scope: resourceScopeSchema.nullable().optional(),
  users: z.array(resourceAccessEntrySchema).optional(),
});

const folderItemSchema = z.object({
  id: z.string().uuid(),
  /** Owner org of this folder (from `folders.owner_org_id`); aligns folders with the other list-item types. */
  orgId: z.string().uuid().optional(),
  /** Display name of the owner org (`orgId`), for cross-org "owned by {Org}" attribution. */
  ownerOrgName: z.string().nullable().optional(),
  ownerId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  name: z.string(),
  position: z.number(),
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  /** Scope from current user's perspective: user (private), org, or external. */
  scope: resourceScopeSchema,
  scopeData: z.record(z.unknown()).optional(),
  /**
   * Direct children the Library lists as rows (subfolders + artifacts), visible
   * in this org context. Chats are not among them — they are never listed
   * individually, so their presence is flagged by `chatCount` instead.
   */
  childrenCount: z.number().int().min(0).optional(),
  /**
   * Whether the folder holds any chats, as `1` or `0` — not how many. Chats
   * collapse to one row regardless of count, so only their presence is reported.
   */
  chatCount: z.number().int().min(0).optional(),
  users: z.array(resourceAccessEntrySchema).optional(),
});

const folderScopeSchema = z.enum(['private', 'shared']);
const sortDirSchema = z.enum(['asc', 'desc']);

/** Item type filter for folder contents (folder, chat, agent). */
const folderItemTypeSchema = z.nativeEnum(ResourceType);

const resourceFolderItemsParamsSchema = z.object({
  folderId: z.string().uuid(),
  /** When set, only include subfolders that match this scope (chats/agents are unchanged). */
  folderScope: folderScopeSchema.optional(),
  /** When set, only include items of this type (same as `itemTypes: [entityType]` when `itemTypes` is omitted). */
  entityType: folderItemTypeSchema.optional(),
  /** When set, only include items whose type is in this list. */
  itemTypes: z.array(folderItemTypeSchema).optional(),
  /** Optional search query; filters by name/title/description (implementation-dependent). */
  search: z.string().optional(),
  /** Optional sort key: updatedAt, createdAt, name, position. */
  sort: z.string().optional(),
  sortDir: sortDirSchema.optional(),
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  /** When `entityType` is `chat` (or `itemTypes` only contains `chat`), only include chats with unread assistant messages. */
  unreadOnly: z.boolean().optional(),
  /** When listing chats, only include chats whose trigger source is in this list. */
  triggerSources: z
    .array(
      z.enum([
        'chat',
        'chat-bot',
        'slack',
        'linear',
        'webhook',
        'warehouse',
        'cron',
        'event',
        'dataset',
        'compaction-summary',
        'workflow',
        'system',
        'api',
      ])
    )
    .optional(),
});

/** One resource item in a folder (subfolder, chat, or agent). Data is always under .data so .data.id is universal. */
const folderResourceItemSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('folder'), data: folderItemSchema }),
  z.object({ type: z.literal('chat'), data: chatItemSchema }),
  z.object({ type: z.literal('agent'), data: agentItemSchema }),
  z.object({ type: z.literal('document'), data: documentItemSchema }),
  z.object({ type: z.literal('workflow'), data: workflowItemSchema }),
  z.object({ type: z.literal('dataset'), data: datasetItemSchema }),
  z.object({
    type: z.literal('workflow_trigger'),
    data: workflowTriggerItemSchema,
  }),
]);

const resourceFolderItemsOutputSchema = z.object({
  items: z.array(folderResourceItemSchema),
  total: z.number().int().min(0),
});

/** List response: discriminated union by type with items array and total count. */
const resourceListOutputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('chat'),
    items: z.array(chatItemSchema),
    total: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('agent'),
    items: z.array(agentItemSchema),
    total: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('folder'),
    items: z.array(folderItemSchema),
    total: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('document'),
    items: z.array(documentItemSchema),
    total: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('workflow'),
    items: z.array(workflowItemSchema),
    total: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('dataset'),
    items: z.array(datasetItemSchema),
    total: z.number().int().min(0),
  }),
  z.object({
    type: z.literal('workflow_trigger'),
    items: z.array(workflowTriggerItemSchema),
    total: z.number().int().min(0),
  }),
]);

export type ResourceGetParams = z.infer<typeof resourceGetParamsSchema>;
export type ResourceOutput = z.infer<typeof resourceOutputSchema>;
export type ResourceListOutput = z.infer<typeof resourceListOutputSchema>;
export type ResourceFolderItemsParams = z.infer<
  typeof resourceFolderItemsParamsSchema
>;
/** One item in a folder listing: `{ type, data }`, uniform across every type. */
export type ResourceFolderItem = z.infer<typeof folderResourceItemSchema>;
export type ResourceFolderItemsOutput = z.infer<
  typeof resourceFolderItemsOutputSchema
>;

/**
 * The parts of a fetched resource {@link isArchivedResourceOutput} reads.
 *
 * Structural rather than {@link ResourceOutput} because the typed HTTP client
 * derives its own response type from the controller, and the two disagree in
 * places that have nothing to do with archiving (a workflow's `graph` is
 * `unknown` there). Both are assignable to this, which is the whole point: one
 * reader for the wire answer whichever end produced it.
 */
type ResourceStatusBody = { status: 'active' | 'archived' };

type StatusBearingResourceOutput =
  | { type: 'chat'; chat: ResourceStatusBody }
  | { type: 'agent'; agent: ResourceStatusBody }
  | { type: 'document'; document: ResourceStatusBody }
  | { type: 'workflow'; workflow: ResourceStatusBody }
  | { type: 'dataset'; dataset: ResourceStatusBody }
  | { type: 'workflow_trigger'; workflowTrigger: ResourceStatusBody }
  | { type: 'folder' };

/**
 * Whether a fetched resource has been archived.
 *
 * Archiving does not hide a resource from `resource.get` — the row is still
 * there and still readable, so the response comes back `ok` with `status:
 * 'archived'` on the body. Every caller that has to tell "put away" from
 * "gone" or "never shared with you" therefore has to read this field rather
 * than the request's success.
 *
 * A switch rather than `output[output.type]`: the discriminant does not name
 * the body's key for every variant (`workflow_trigger` carries
 * `workflowTrigger`), and folders have no `status` to read at all — they are
 * the one type the archive action cannot target.
 */
export const isArchivedResourceOutput = (
  output: StatusBearingResourceOutput
): boolean => {
  switch (output.type) {
    case 'chat':
      return output.chat.status === 'archived';
    case 'agent':
      return output.agent.status === 'archived';
    case 'document':
      return output.document.status === 'archived';
    case 'workflow':
      return output.workflow.status === 'archived';
    case 'dataset':
      return output.dataset.status === 'archived';
    case 'workflow_trigger':
      return output.workflowTrigger.status === 'archived';
    case 'folder':
      return false;
    default:
      return Assert.unreachable(output);
  }
};

/**
 * The artifact type and id for the Organize review panel. One definition shared
 * by every producer (the Library "Organize" button, the Coco organize tool, the
 * draft service) and the client renderer, so the id the panel keys on cannot
 * drift between where it is written and where it is read.
 */
export const ORGANIZE_ARTIFACT_TYPE = 'organize-library';
export const organizeArtifactId = (conversationId: string): string =>
  `organize-${conversationId}`;

/** Which library an organize chat works on: the actor's own, or the org's shared one. */
export type OrganizeLibraryMode = 'personal' | 'shared';

/**
 * The library an organize artifact payload was opened for. Undefined when the
 * payload doesn't say — chats opened before the mode was recorded, and panels
 * Coco opens itself.
 */
export const organizeModeOf = (
  payload: unknown
): OrganizeLibraryMode | undefined => {
  if (typeof payload !== 'object' || payload === null || !('mode' in payload)) {
    return undefined;
  }
  return payload.mode === 'personal' || payload.mode === 'shared'
    ? payload.mode
    : undefined;
};

/**
 * The chat and panel title for an organize chat, naming which library it
 * organizes so an admin's "my" and "shared" chats aren't indistinguishable.
 */
export const organizeLibraryTitle = (
  mode: OrganizeLibraryMode | undefined
): string => {
  switch (mode) {
    case 'personal':
      return 'Organize my library';
    case 'shared':
      return 'Organize shared library';
    case undefined:
      return 'Organize library';
    default:
      return Assert.unreachable(mode);
  }
};
